import React, { useEffect, useState } from 'react';
import { Users, X } from 'lucide-react';
import FindBuyersForProperty from '../../analytics/components/FindBuyersForProperty';

/**
 * "Find Buyers" on a re-listing opportunity (Christian Gravias, Monday
 * 13354971743, 24 Oct 2026: "add a Find Buyers button where we can match
 * buyers for each property"). Opens the Find Buyers panel on this property and
 * runs the match straight away: its suburb plus the surrounding ones, its
 * guide, bedrooms, bathrooms, car spaces and type, all of which the agent can
 * widen. The number on the button is the nightly buyer score (how many CRM
 * buyers fit), the same search the panel opens with.
 *
 * 27 Oct 2026 (same item): buyers who marked only "Melbourne" are a broad
 * match, not as strong as one who named the suburb. The button leads with the
 * strong ones (named this suburb) and its tooltip splits the rest into
 * possible (a suburb nearby) and broad (Melbourne only).
 */

// The row as the panel's "Not in Collings" property (never a CRM property id).
export const findBuyersProperty = (row) => ({
  address: row.address,
  suburb: row.suburb,
  state: row.state || 'VIC',
  latitude: row.latitude ?? null,
  longitude: row.longitude ?? null,
  price: row.current_guide ?? null,
  beds: row.bedrooms || null,
  baths: row.bathrooms || null,
  cars: row.car_spaces ?? null,
  property_type: row.property_type === 'other' ? '' : row.property_type,
});

export const buyerCountLabel = (n) => (n === 1 ? '1 buyer' : `${n} buyers`);

const hasTiers = (row) => row.buyer_strong != null;

export const buyerTierTitle = (row) =>
  `${buyerCountLabel(row.buyer_matches)} in the CRM fit this property: ${row.buyer_strong} strong (named this suburb), ` +
  `${row.buyer_possible} possible (a suburb nearby), ${row.buyer_broad} broad (Melbourne only). ` +
  `Best match ${row.buyer_top_score}/100`;

const RelistingFindBuyersButton = ({ row }) => {
  const [open, setOpen] = useState(false);
  const count = row.buyer_matches;

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => e.key === 'Escape' && setOpen(false);
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open]);

  const title = count == null
    ? 'Match this property against the buyers in the CRM'
    : !count
    ? 'No buyer in the CRM fits this property yet'
    : hasTiers(row)
    ? buyerTierTitle(row)
    : `${buyerCountLabel(count)} in the CRM fit this property (best match ${row.buyer_top_score}/100)`;
  const strong = hasTiers(row) && count ? row.buyer_strong : null;

  return (
    <>
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          setOpen(true);
        }}
        title={title}
        className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg text-[12px] font-semibold text-gray-600 border border-gray-200 bg-white hover:border-gray-300 whitespace-nowrap"
      >
        <Users className="w-3.5 h-3.5" />
        Find Buyers
        {strong != null ? (
          <span className="ml-0.5 inline-flex items-center gap-1 text-[10px] font-bold">
            <span className={`rounded-full px-1.5 ${strong ? 'bg-teal-100 text-teal-700' : 'bg-gray-100 text-gray-400'}`}>
              {strong} strong
            </span>
            {count - strong > 0 && <span className="text-gray-400">+{count - strong}</span>}
          </span>
        ) : count != null && (
          <span
            className={`ml-0.5 rounded-full px-1.5 text-[10px] font-bold ${
              count ? 'bg-teal-100 text-teal-700' : 'bg-gray-100 text-gray-400'
            }`}
          >
            {count}
          </span>
        )}
      </button>
      {open && (
        <div
          className="fixed inset-0 z-50 flex items-start justify-center bg-black/40 p-2 sm:p-6 overflow-y-auto"
          onClick={(e) => e.stopPropagation()}
          role="dialog"
          aria-modal="true"
          aria-label={`Find buyers for ${row.address}`}
        >
          <div className="relative w-full max-w-6xl rounded-xl bg-gray-50 shadow-xl">
            <div className="sticky top-0 z-10 flex items-center justify-between gap-3 rounded-t-xl border-b border-gray-200 bg-white px-4 py-3">
              <div className="min-w-0">
                <div className="text-[11px] font-semibold uppercase tracking-wide text-gray-400">Find buyers for</div>
                <div className="truncate text-sm font-bold text-gray-900">{row.address}</div>
              </div>
              <button
                type="button"
                onClick={() => setOpen(false)}
                className="flex-shrink-0 rounded-lg p-1.5 text-gray-400 hover:bg-gray-100 hover:text-gray-700"
                aria-label="Close"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-3 sm:p-4">
              <FindBuyersForProperty initialAddress={findBuyersProperty(row)} autoRun />
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default RelistingFindBuyersButton;
